/**
 * App route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import type { ReactNode } from 'react'
import CartPage from './CartPage'
import CategoryPage from './CategoryPage'
import CheckoutPage from './CheckoutPage'
import CmsPageView from './CmsPageView'
import InternalRouteChrome from './InternalRouteChrome'
import ProductPage from './ProductPage'
import SearchResultsPage from './SearchResultsPage'
import RuntimeHomeBlocksView from './RuntimeHomeBlocksView'
import ErrorCodePage from '../../components/ErrorCodePage'
import { CustomerRouteView, resolveCustomerRouteMode } from '../../customer-auth'
import { useShopAppRuntime } from '../hooks/useShopAppRuntime'

type ShopRuntime = ReturnType<typeof useShopAppRuntime>

type Props = {
  runtime: ShopRuntime
  customerRouteMode: ReturnType<typeof resolveCustomerRouteMode>
  shouldRenderRuntimeFooter: boolean
}

const AppRouteContent = ({ runtime, customerRouteMode, shouldRenderRuntimeFooter }: Props) => {
  const isCartRoute = runtime.route.mode === 'page' && runtime.route.slug.trim().toLowerCase() === 'cart'
  const isCheckoutRoute = runtime.route.mode === 'page' && runtime.route.slug.trim().toLowerCase() === 'checkout'
  const isSearchRoute = runtime.route.mode === 'page' && runtime.route.slug.trim().toLowerCase() === 'search'
  const wrapInternalRoute = (content: ReactNode) => (
    <InternalRouteChrome
      homeSettings={runtime.homeSettings}
      isAdmin={runtime.isAdmin}
      shouldRenderRuntimeFooter={shouldRenderRuntimeFooter}
    >
      {content}
    </InternalRouteChrome>
  )

  if (customerRouteMode && runtime.route.mode === 'page') {
    return wrapInternalRoute(
      <CustomerRouteView
        mode={customerRouteMode}
        currentSlug={runtime.route.slug}
        onNavigate={runtime.navigateTo}
      />,
    )
  }

  if (isCartRoute) {
    return wrapInternalRoute(<CartPage />)
  }

  if (isCheckoutRoute) {
    return wrapInternalRoute(<CheckoutPage />)
  }

  if (isSearchRoute) {
    return wrapInternalRoute(<SearchResultsPage />)
  }

  if (runtime.activeError) {
    return wrapInternalRoute(
      <ErrorCodePage
        code={runtime.activeError.code}
        message={runtime.activeError.message}
        onGoCatalog={() => {
          if (typeof window !== 'undefined' && window.history.length > 1) {
            window.history.back()
            return
          }
          runtime.navigateTo('/search')
        }}
        onContactSupport={() => runtime.navigateTo('/dashboard/support')}
        onGoHome={() => runtime.navigateTo('/')}
        onGoBack={() => {
          if (typeof window !== 'undefined') window.history.back()
        }}
        onReload={() => {
          if (typeof window !== 'undefined') window.location.reload()
        }}
      />,
    )
  }

  if (runtime.route.mode !== 'home' && (runtime.route.mode === 'product' || runtime.productData)) {
    return wrapInternalRoute(
      <ProductPage
        productData={runtime.productData}
        productLoading={runtime.productLoading}
        productError={runtime.productError}
        productGallery={runtime.productGallery}
        activeProductImage={runtime.activeProductImage}
        onSelectProductImage={runtime.setSelectedProductImage}
        productFieldDefinitions={runtime.productFieldDefinitions}
      />,
    )
  }

  if (runtime.route.mode !== 'home' && runtime.categoryRouteData) {
    return wrapInternalRoute(
      <CategoryPage
        categoryRouteData={runtime.categoryRouteData}
        productFieldDefinitions={runtime.productFieldDefinitions}
      />,
    )
  }

  if (runtime.route.mode !== 'home') {
    return wrapInternalRoute(
      <CmsPageView
        route={runtime.route}
        pageData={runtime.pageData}
        pageLoading={runtime.pageLoading}
        pageError={runtime.pageError}
      />,
    )
  }

  const homePageData =
    runtime.homePageData &&
    !runtime.homePageLoading &&
    !runtime.homePageError &&
    runtime.hasHomeBlocks
      ? runtime.homePageData
      : null

  return (
    <RuntimeHomeBlocksView
      homePageData={homePageData}
      homeSettings={runtime.homeSettings}
      isAdmin={runtime.isAdmin}
      slides={runtime.slides}
      currentSlide={runtime.currentSlide}
      currentIndex={runtime.currentIndex}
      onPrev={runtime.goPrev}
      onNext={runtime.goNext}
      onSelectSlide={runtime.setCurrentIndex}
      shouldRenderRuntimeFooter={shouldRenderRuntimeFooter}
    />
  )
}

export default AppRouteContent
